import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { BaseFilterButton } from 'components/base-table/filters';
import { usePageParams } from 'hooks/use-page-params';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TOption = { value: number; label: string };
type TInputs = { supplier: TOption | undefined };

const defaultValues: TInputs = { supplier: undefined };

const getSupplierDetails = async (supplierId: string) => {
  const { data } = await http.get(`suppliers/${supplierId}`);
  return data;
};

export const SupplierFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  useEffect(() => {
    if (params?.supplierId) setIsActive.on();
    else setIsActive.off();
  }, [params?.supplierId]);

  const { control, handleSubmit, formState, reset } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  const updateValues = async () => {
    if (params?.supplierId) {
      const { data } = await getSupplierDetails(params.supplierId as string);
      reset({ supplier: { value: data?.id, label: data?.name ?? `Supplier #${data?.id}` } });
    } else {
      reset({ supplier: undefined });
    }
  };

  useEffect(() => {
    updateValues();
  }, [params?.supplierId]);

  const onSubmit = (data: TInputs) => {
    params.supplierId = (data.supplier?.value as unknown as string) ?? '';
    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    updateValues();
  };

  const handleDisableFilterButton = () => !Object.keys(formState.dirtyFields).length;

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const loadOptions = useCallback((inputValue: string, callback: (options: TOption[]) => void) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // BE's $contL filter rejects empty values with 400, so don't fire until the user has typed something meaningful.
    // Also matches the house async-search pattern (see use-search-bnpl-restaurants-api.ts).
    if (inputValue.trim().length < 2) {
      callback([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      http
        .get('suppliers', {
          params: { filter: `name||$contL||${inputValue}` },
        })
        .then((res) => {
          const values: TOption[] =
            res?.data?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.name ?? `Supplier #${item?.id}`,
            })) ?? [];
          callback(values);
        })
        .catch(() => callback([]));
    }, 300);
  }, []);

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='supplier'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Supplier</FormLabel>
                <ChakraAsyncSelect
                  id='supplierId'
                  onChange={onChange}
                  name={name}
                  onBlur={onBlur}
                  value={value}
                  loadOptions={loadOptions}
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
